import { Link } from 'react-router-dom';
import moment from 'moment';
import { PagesRoutes } from '../../../../routing/PagesRoutes';
import {
    buildConsolidationNavigationState,
    editShipmentFromConsolidationPath,
    newHouseShipmentPath
} from './shipmentFormNavigation';
import BackButton from '../../../shared/SmallComponents/BackButton';
import Button from '../../../shared/SmallComponents/Button';
import CopyToClipboard from '../../../shared/CopyToClipboard';
import { RightArrowIcon } from '../../../shared/Grid/CellRenderer/CellRenderer';
import ShipmentStation from '../../../shared/Grid/specialColumns/ShipmentStation';
import { styled } from '../../../shared/Theme/theme';

type Summary = {
    titleSuffix: string;
    status: string;
    stateLabel: string;
    stateColor?: string;
    originStation?: string;
    destinationStation?: string;
    originCountry?: string;
    destinationCountry?: string;
    flightOrVoyage: string;
    handlingAgentId?: string;
    handlingAgentName?: string;
    etd?: Date;
    eta?: Date;
    shipmentCount: number;
    totalWeight: number;
    totalVolume: number;
    weightUnit: string;
    volumeUnit: string;
};

type Props = {
    master: string;
    summary: Summary;
    firstShipmentId: string;
    housePrefillInitialValues: Record<string, unknown>;
};

const ConsolidationDetailsHeader = ({ master, summary, firstShipmentId, housePrefillInitialValues }: Props) => {
    const formatDate = (date?: Date) => (date ? moment(date).format('DD/MM/YYYY') : '-');
    const isActiveStatus = summary.status !== 'DRAFT' && summary.status !== 'DELIVERED';

    const origin = summary.originStation || '-';
    const destination = summary.destinationStation || '-';
    const originCountry = summary.originCountry || '';
    const destinationCountry = summary.destinationCountry || '';

    return (
        <HeaderWrapper>
            <BackRow>
                <BackButton toPath={PagesRoutes.ShipmentsConsolidations} text="Back to Consolidations" />
            </BackRow>

            <TitleRow>
                <TitleGroup>
                    <PageTitle>Consolidation ({summary.titleSuffix})</PageTitle>
                    <StatusBadge $color={summary.stateColor} $active={isActiveStatus}>
                        {summary.stateLabel}
                    </StatusBadge>
                </TitleGroup>
                <HeaderActions>
                    <Link
                        to={editShipmentFromConsolidationPath(firstShipmentId, master)}
                        state={buildConsolidationNavigationState(master)}
                    >
                        <Button buttonType="hollow" margin="0 8px 0 0" minHeight="32px" padding="6px 14px">
                            <>
                                <span className="material-icons" style={{ fontSize: 16, marginRight: 6, verticalAlign: 'middle' }}>
                                    edit
                                </span>
                                Edit Master
                            </>
                        </Button>
                    </Link>
                    <Link
                        to={newHouseShipmentPath(master)}
                        state={{
                            initialValues: housePrefillInitialValues,
                            ...buildConsolidationNavigationState(master)
                        }}
                    >
                        <Button buttonType="filled" minHeight="32px" padding="6px 16px">
                            Add House Shipment
                        </Button>
                    </Link>
                </HeaderActions>
            </TitleRow>

            <MasterRow>
                <MasterLabel>Master AWB:</MasterLabel>
                <MasterValue>{master}</MasterValue>
                <CopyIconWrap>
                    <CopyToClipboard value={master} />
                </CopyIconWrap>
            </MasterRow>

            <MetricsRow>
                <MetricGroup>
                    <MetricLabel>Route:</MetricLabel>
                    <RouteValue>
                        {originCountry ? <ShipmentStation country={originCountry} port={origin} /> : <span>{origin}</span>}
                        <RouteArrow aria-hidden />
                        {destinationCountry ? (
                            <ShipmentStation country={destinationCountry} port={destination} />
                        ) : (
                            <span>{destination}</span>
                        )}
                    </RouteValue>
                </MetricGroup>
                <MetricPipe>|</MetricPipe>
                <MetricGroup>
                    <MetricLabel>Handling Agent:</MetricLabel>
                    <MetricValue>{summary.handlingAgentName || '—'}</MetricValue>
                </MetricGroup>
                <MetricPipe>|</MetricPipe>
                <MetricGroup>
                    <MetricLabel>Flight:</MetricLabel>
                    <MetricValue>{summary.flightOrVoyage}</MetricValue>
                </MetricGroup>
                <MetricPipe>|</MetricPipe>
                <MetricGroup>
                    <MetricLabel>ETD:</MetricLabel>
                    <MetricValue>{formatDate(summary.etd)}</MetricValue>
                </MetricGroup>
                <MetricPipe>|</MetricPipe>
                <MetricGroup>
                    <MetricLabel>ETA:</MetricLabel>
                    <MetricValue>{formatDate(summary.eta)}</MetricValue>
                </MetricGroup>
                <MetricPipe>|</MetricPipe>
                <MetricGroup>
                    <MetricLabel>House Shipments:</MetricLabel>
                    <MetricValue>{summary.shipmentCount}</MetricValue>
                </MetricGroup>
                <MetricPipe>|</MetricPipe>
                <MetricGroup>
                    <MetricLabel>Total Weight:</MetricLabel>
                    <MetricValue>
                        {summary.totalWeight} {summary.weightUnit}
                    </MetricValue>
                </MetricGroup>
                <MetricPipe>|</MetricPipe>
                <MetricGroup>
                    <MetricLabel>Total Volume:</MetricLabel>
                    <MetricValue>
                        {summary.totalVolume} {summary.volumeUnit}
                    </MetricValue>
                </MetricGroup>
            </MetricsRow>
        </HeaderWrapper>
    );
};

const HeaderWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 20px 18px 8px 48px;
    background: #fff;
    border: none;
    box-shadow: none;
`;

const BackRow = styled.div`
    display: flex;
    align-items: center;
`;

const TitleRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
`;

const TitleGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
`;

const HeaderActions = styled.div`
    display: flex;
    align-items: center;
    flex-shrink: 0;
`;

const PageTitle = styled.h1`
    margin: 0;
    font-size: 20px;
    font-weight: 700;
    color: ${(props) => props.theme.colors.pageTitle};
    line-height: 1.3;
`;

const StatusBadge = styled.span<{ $active?: boolean; $color?: string }>`
    border-radius: 10px;
    padding: 2px 8px;
    font-weight: 600;
    font-size: 12px;
    line-height: 1.5;
    white-space: nowrap;
    background: ${(props) =>
        props.$color ? `${props.$color}22` : props.$active ? '#e8f5e9' : props.theme.colors.secondaryOpacity};
    color: ${(props) => (props.$color ? props.$color : props.$active ? '#2e7d32' : props.theme.colors.secondary)};
`;

const MasterRow = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    line-height: 1.4;
`;

const MasterLabel = styled.span`
    color: ${(props) => props.theme.colors.lightGray};
    font-weight: 400;
`;

const MasterValue = styled.span`
    color: ${(props) => props.theme.colors.link};
    font-weight: 600;
`;

const CopyIconWrap = styled.span`
    display: inline-flex;
    align-items: center;
    margin-left: 2px;

    .material-icons {
        font-size: 15px !important;
        color: ${(props) => props.theme.colors.lightGray};
    }
`;

const MetricsRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    row-gap: 6px;
    font-size: 14px;
    line-height: 1.4;
    margin-bottom: 20px;
`;

const MetricGroup = styled.span`
    display: inline-flex;
    align-items: baseline;
    gap: 6px;
    white-space: nowrap;
`;

const MetricLabel = styled.span`
    color: ${(props) => props.theme.colors.lightGray};
    font-weight: 400;
`;

const MetricValue = styled.span`
    color: ${(props) => props.theme.colors.pageTitle};
    font-weight: 600;
`;

const RouteValue = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: ${(props) => props.theme.colors.pageTitle};
    font-weight: 600;
`;

const RouteArrow = styled(RightArrowIcon)`
    flex-shrink: 0;
    margin: 0 2px;
`;

const MetricPipe = styled.span`
    color: #c5c5c5;
    padding: 0 14px;
    font-weight: 300;
    user-select: none;
    line-height: 1;

    @media screen and (max-width: 900px) {
        display: none;
    }
`;

export default ConsolidationDetailsHeader;
